import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Zap, Loader2, Upload, ArrowRight, FileSpreadsheet, CheckCircle2, Clock, AlertTriangle, RotateCw } from 'lucide-react';

export default function TodayDashboard() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [totals, setTotals] = useState({ total_bids: 0, items_analyzed: 0, submitted: 0, high: 0, buy: 0 });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const fetchActivity = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      // 2 Nov 2026 small fix: breakdowns=0 — the dashboard never uses the breakdown
      // aggregation, so skip loading every completed job's full results array.
      const r = await api.get('/bid-engine/history', { params: { breakdowns: 0 } });
      setJobs(r.data.jobs || []);
      // 21 Oct 2026 bug fix: Total Bids / Items Analyzed are now computed server-side via a
      // MongoDB aggregation (not by summing every loaded job client-side) — this is what used
      // to silently cap "Total Bids" at 300 once more than 300 bids existed.
      setTotals(r.data.totals || { total_bids: 0, items_analyzed: 0, submitted: 0, high: 0, buy: 0 });
    }
    catch (e) { setLoadError(true); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  const recent = jobs.slice(0, 10);

  return (
    <div className="space-y-6" data-testid="home-bid-activity">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Zap className="h-6 w-6 text-primary" />Bid Engine Activity</h1>
          <p className="text-muted-foreground text-sm">Recent AI-powered bid analyses across your account</p>
        </div>
        <Button className="rounded-sm" onClick={() => navigate('/bid-engine')} data-testid="home-new-bid-btn"><Upload className="h-4 w-4 mr-2" />New Bid</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
      ) : loadError ? (
        <Card className="rounded-sm" data-testid="home-activity-error">
          <CardContent className="py-16 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto text-amber-500/70 mb-3" />
            <p className="text-muted-foreground mb-4">Couldn't load bid activity</p>
            <Button variant="outline" className="rounded-sm" onClick={fetchActivity} data-testid="home-activity-retry-btn">
              <RotateCw className="h-4 w-4 mr-2" />Retry
            </Button>
          </CardContent>
        </Card>
      ) : jobs.length === 0 ? (
        <Card className="rounded-sm"><CardContent className="py-16 text-center">
          <FileSpreadsheet className="h-12 w-12 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground mb-4">No bids processed yet</p>
          <Button className="rounded-sm" onClick={() => navigate('/bid-engine')} data-testid="home-empty-cta-btn">Process Your First Bid</Button>
        </CardContent></Card>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              { label: 'Total Bids', value: totals.total_bids },
              { label: 'Submitted', value: totals.submitted },
              { label: 'Items Analyzed', value: totals.items_analyzed },
              { label: 'HIGH Confidence', value: totals.high },
              { label: 'BUY Recommended', value: totals.buy },
            ].map(s => (
              <Card key={s.label} className="rounded-sm" data-testid={`home-stat-${s.label.replace(/\s+/g, '-').toLowerCase()}`}>
                <CardContent className="p-4">
                  <p className="text-2xl font-bold">{s.value}</p>
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="rounded-sm">
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow className="bg-muted/50">
                  <TableHead className="text-xs font-medium">Vendor</TableHead>
                  <TableHead className="text-xs font-medium">Uploaded By</TableHead>
                  <TableHead className="text-xs font-medium">Date</TableHead>
                  <TableHead className="text-xs font-medium text-right">Items</TableHead>
                  <TableHead className="text-xs font-medium text-right">HIGH</TableHead>
                  <TableHead className="text-xs font-medium text-right">BUY</TableHead>
                  <TableHead className="text-xs font-medium">Status</TableHead>
                  <TableHead className="text-xs font-medium text-right">Action</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {recent.map(j => (
                    <TableRow key={j.id} data-testid={`home-bid-row-${j.id}`}>
                      <TableCell className="text-sm font-medium">{j.vendor_name_canonical || j.vendor_name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{j.uploaded_by}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{j.created_at ? new Date(j.created_at).toLocaleDateString() : '—'}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{j.stats?.total ?? '—'}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{j.stats?.high ?? '—'}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{j.stats?.buy ?? '—'}</TableCell>
                      <TableCell>
                        {j.submitted ? (
                          <Badge className="bg-emerald-100 text-emerald-800 rounded-sm text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1" />Submitted</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800 rounded-sm text-[10px]"><Clock className="h-3 w-3 mr-1" />Draft</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" className="h-7 text-xs rounded-sm" onClick={() => navigate(`/bid-engine?job=${j.id}`)} data-testid={`home-view-bid-${j.id}`}>
                          View<ArrowRight className="h-3.5 w-3.5 ml-1" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
